import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  X,
  History,
  TrendingUp,
  FileText,
  ChevronRight,
  ArrowRight,
  Sparkles,
  Tag,
  Clock
} from 'lucide-react';
import { Product } from '../types';
import { getSafeProductFirstImage } from '../lib/productImages';
import {
  performSmartSearch,
  getRecentSearches,
  saveRecentSearch,
  removeRecentSearch,
  clearAllRecentSearches
} from '../lib/searchEngine';

interface SearchAutocompleteProps {
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  onSelectProduct: (product: Product) => void;
  onPerformSearch: (query: string) => void;
  products: Product[];
  darkMode: boolean;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  autoFocus?: boolean;
}

export const SearchAutocomplete: React.FC<SearchAutocompleteProps> = ({
  searchQuery,
  setSearchQuery,
  onSelectProduct,
  onPerformSearch,
  products,
  darkMode,
  placeholder = 'Search USA Passport, ID Card, Bank Statement PSD...',
  className = '',
  size = 'md',
  autoFocus = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent searches on mount & focus
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Compute live search suggestions (up to 5 items)
  const searchResponse = performSmartSearch(products, searchQuery);
  const suggestions = searchQuery.trim() ? searchResponse.results.slice(0, 5) : [];

  // Popular suggested terms
  const popularTerms = [
    'USA Passport PSD',
    'UK ID Card',
    'California Driver License',
    'Bank Statement PSD',
    'Gold Certificate',
    'Utility Bill Word',
  ];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle Search Submission
  const handleSubmit = (termToSearch?: string) => {
    const term = (termToSearch !== undefined ? termToSearch : searchQuery).trim();
    if (!term) return;
    saveRecentSearch(term);
    setRecentSearches(getRecentSearches());
    setIsOpen(false);
    onPerformSearch(term);
  };

  // Handle Keyboard Navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true);
      }
      return;
    }

    const totalItems = suggestions.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        const selectedProd = suggestions[selectedIndex];
        onSelectProduct(selectedProd);
        saveRecentSearch(selectedProd.title);
        setIsOpen(false);
      } else {
        handleSubmit();
      }
    }
  };

  const handleRemoveRecent = (e: React.MouseEvent, term: string) => {
    e.stopPropagation();
    const updated = removeRecentSearch(term);
    setRecentSearches(updated);
  };

  const handleClearAllRecent = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearAllRecentSearches();
    setRecentSearches([]);
  };

  // Helper for size styles
  const inputPadding = size === 'lg' ? 'py-3.5 pl-11 pr-24 text-sm sm:text-base' : size === 'sm' ? 'py-1.5 pl-8 pr-16 text-xs' : 'py-2.5 pl-10 pr-20 text-xs sm:text-sm';
  const iconSize = size === 'lg' ? 'w-5 h-5 left-3.5' : size === 'sm' ? 'w-3.5 h-3.5 left-2.5' : 'w-4 h-4 left-3';

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Input Container */}
      <div className="relative group flex items-center w-full">
        <Search className={`absolute top-1/2 -translate-y-1/2 text-cyan-400 flex-shrink-0 z-10 pointer-events-none ${iconSize}`} />

        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          autoFocus={autoFocus}
          onChange={e => {
            setSearchQuery(e.target.value);
            setSelectedIndex(-1);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setRecentSearches(getRecentSearches());
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="true"
          className={`w-full ${inputPadding} rounded-xl font-medium transition-all duration-200 focus:outline-none ${
            darkMode
              ? 'bg-slate-900/90 border border-slate-700/80 text-white placeholder:text-slate-500 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20'
              : 'bg-white/95 border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'
          } shadow-sm`}
        />

        {/* Clear input or submit search button */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10">
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery('');
                setSelectedIndex(-1);
                inputRef.current?.focus();
              }}
              className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              title="Clear text"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            type="button"
            onClick={() => handleSubmit()}
            className="px-2.5 py-1 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white text-xs font-bold shadow-md transition-all active:scale-95 flex items-center gap-1"
          >
            <span>Search</span>
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Autocomplete Suggestions Overlay */}
      {isOpen && (
        <div
          className={`absolute left-0 right-0 top-full mt-2 rounded-2xl border shadow-2xl z-50 overflow-hidden transition-all duration-200 animate-in fade-in slide-in-from-top-2 ${
            darkMode
              ? 'bg-slate-900/95 border-slate-700/80 text-white'
              : 'bg-white/98 border-slate-200 text-slate-900'
          }`}
        >
          {/* CASE 1: Query is typed */}
          {searchQuery.trim() ? (
            <div>
              {suggestions.length > 0 ? (
                <div className="p-2 divide-y divide-white/5">
                  <div className="px-3 py-1.5 text-[11px] font-bold tracking-wider uppercase text-cyan-400 flex items-center justify-between">
                    <span>Matching Templates ({searchResponse.totalMatches})</span>
                    <span className="text-[10px] text-slate-400 font-mono">{searchResponse.executionTimeMs}ms</span>
                  </div>

                  <div className="pt-1 space-y-1">
                    {suggestions.map((prod, index) => {
                      const isSelected = index === selectedIndex;
                      return (
                        <div
                          key={`${prod.id}-${index}`}
                          onClick={() => {
                            onSelectProduct(prod);
                            saveRecentSearch(prod.title);
                            setIsOpen(false);
                          }}
                          onMouseEnter={() => setSelectedIndex(index)}
                          className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-colors ${
                            isSelected
                              ? darkMode
                                ? 'bg-cyan-500/20 text-white border border-cyan-500/30'
                                : 'bg-blue-50 text-blue-900 border border-blue-200'
                              : darkMode
                              ? 'hover:bg-slate-800/60'
                              : 'hover:bg-slate-100'
                          }`}
                        >
                          <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-slate-800 border border-white/10">
                            <img
                              src={getSafeProductFirstImage(prod)}
                              alt={prod.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className="text-xs font-bold truncate group-hover:text-cyan-400 transition-colors">
                              {prod.title}
                            </h4>
                            <div className="flex items-center gap-2 mt-0.5 text-[11px] text-slate-400">
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700/60 font-semibold text-cyan-300">
                                {prod.category}
                              </span>
                              {prod.country && (
                                <span className="truncate">{prod.country}</span>
                              )}
                              <span>•</span>
                              <span className="text-amber-400 font-bold">${prod.price}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            {(prod.formats || ['PSD']).slice(0, 2).map(fmt => (
                              <span key={fmt} className="px-1.5 py-0.5 text-[10px] font-extrabold rounded bg-slate-800 text-slate-300 border border-white/10">
                                {fmt}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Submit All Results Footer */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-blue-600/30 to-indigo-600/30 hover:from-blue-600/50 hover:to-indigo-600/50 border border-blue-500/30 text-cyan-300 font-bold text-xs flex items-center justify-center gap-2 transition-all"
                    >
                      <span>View all {searchResponse.totalMatches} results for "{searchQuery}"</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center">
                  <p className="text-sm font-semibold text-slate-400">No direct matches found for "{searchQuery}"</p>
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    className="mt-3 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold text-xs shadow-md"
                  >
                    Search in Marketplace Engine
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* CASE 2: Input is empty - Show Recent Searches, Popular Searches, Trending */
            <div className="p-4 space-y-4 max-h-[420px] overflow-y-auto custom-scrollbar">
              {/* Recent Searches */}
              {recentSearches.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                      <History className="w-3.5 h-3.5 text-cyan-400" />
                      Recent Searches
                    </span>
                    <button
                      type="button"
                      onClick={handleClearAllRecent}
                      className="text-[10px] text-slate-400 hover:text-rose-400 transition-colors font-medium"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {recentSearches.map((term, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setSearchQuery(term);
                          handleSubmit(term);
                        }}
                        className={`group inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium cursor-pointer transition-colors border ${
                          darkMode
                            ? 'bg-slate-800/80 border-slate-700/80 hover:bg-slate-700 text-slate-200'
                            : 'bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-800'
                        }`}
                      >
                        <Clock className="w-3 h-3 text-slate-400 group-hover:text-cyan-400" />
                        <span>{term}</span>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveRecent(e, term)}
                          className="p-0.5 ml-0.5 text-slate-400 hover:text-rose-400 rounded transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Searches */}
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2">
                  <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
                  Popular Search Terms
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {popularTerms.map((term, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => {
                        setSearchQuery(term);
                        handleSubmit(term);
                      }}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border flex items-center gap-1 ${
                        darkMode
                          ? 'bg-slate-800/40 border-slate-700/60 hover:bg-slate-700/80 text-cyan-300 hover:border-cyan-400'
                          : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      <Tag className="w-3 h-3 opacity-70" />
                      <span>{term}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Trending Templates Quick Highlights */}
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                  Trending Master Templates
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {products.slice(0, 4).map((p, idx) => (
                    <div
                      key={`${p.id}-${idx}`}
                      onClick={() => {
                        onSelectProduct(p);
                        setIsOpen(false);
                      }}
                      className={`flex items-center gap-2.5 p-2 rounded-xl cursor-pointer border transition-all ${
                        darkMode
                          ? 'bg-slate-800/40 border-slate-700/50 hover:bg-slate-800'
                          : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      <img
                        src={getSafeProductFirstImage(p)}
                        alt={p.title}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 border border-white/10"
                        loading="lazy"
                        
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold truncate">{p.title}</p>
                        <p className="text-[10px] text-cyan-400 font-semibold">${p.price} • {p.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
